import { useState } from 'react';
import { Percent, Filter, CheckSquare, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { useToast } from '@/providers/ToastProvider';
import { CommissionTable } from '../components/CommissionTable';
import {
  useAdminCommissions,
  useApproveCommission,
  useCancelCommission,
  useBlockCommission,
  useBulkApproveCommissions,
  useProcessCommissionExpiration,
} from '../hooks/useAdminCommissions';

export default function AdminCommissionsPage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data, isLoading } = useAdminCommissions({
    page,
    limit: 10,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    from: dateFrom || undefined,
    to: dateTo || undefined,
  });

  const approveMutation = useApproveCommission();
  const cancelMutation = useCancelCommission();
  const blockMutation = useBlockCommission();
  const bulkApproveMutation = useBulkApproveCommissions();
  const processExpirationMutation = useProcessCommissionExpiration();

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      toast({ title: 'Comissão aprovada!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao aprovar comissão', variant: 'error' });
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelMutation.mutateAsync({ id });
      toast({ title: 'Comissão cancelada', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao cancelar comissão', variant: 'error' });
    }
  };

  const handleBlock = async (id: string) => {
    try {
      await blockMutation.mutateAsync({ id });
      toast({ title: 'Comissão bloqueada', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao bloquear comissão', variant: 'error' });
    }
  };

  const handleBulkApprove = async () => {
    try {
      const result = await bulkApproveMutation.mutateAsync(Array.from(selectedIds));
      toast({ title: `${result.approved} comissões aprovadas!`, variant: 'success' });
      setSelectedIds(new Set());
    } catch {
      toast({ title: 'Erro ao aprovar comissões', variant: 'error' });
    }
  };

  const handleProcessExpiration = async () => {
    try {
      const result = await processExpirationMutation.mutateAsync();
      toast({ title: `${result.processed} comissões processadas por expiração`, variant: 'success' });
    } catch {
      toast({ title: 'Erro ao processar expiração', variant: 'error' });
    }
  };

  const toggleSelectId = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const commissionsList = data?.data || (data as any)?.items || [];

  const toggleSelectAll = () => {
    if (selectedIds.size === commissionsList.length && commissionsList.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(commissionsList.map((c: any) => c.id)));
    }
  };

  const pendingCount = commissionsList.filter((c: any) => c.status === 'PENDING').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Percent className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Comissões</h1>
          {pendingCount > 0 && (
            <span className="px-2 py-1 bg-warning/10 text-warning text-sm rounded-full">
              {pendingCount} pendentes
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleProcessExpiration}
            disabled={processExpirationMutation.isPending}
          >
            <Clock className="h-4 w-4 mr-2" />
            Processar Expiração
          </Button>
          {selectedIds.size > 0 && (
            <Button
              variant="primary"
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
              isLoading={bulkApproveMutation.isPending}
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Aprovar Selecionadas ({selectedIds.size})
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Status</SelectItem>
                <SelectItem value="PENDING">Pendente</SelectItem>
                <SelectItem value="APPROVED">Aprovada</SelectItem>
                <SelectItem value="AVAILABLE">Disponível</SelectItem>
                <SelectItem value="PAID">Paga</SelectItem>
                <SelectItem value="CANCELLED">Cancelada</SelectItem>
                <SelectItem value="BLOCKED">Bloqueada</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">De:</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Até:</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <CommissionTable
        commissions={commissionsList}
        isLoading={isLoading}
        total={data?.meta?.total ?? (data as any)?.total ?? 0}
        page={page}
        pageSize={data?.meta?.perPage ?? (data as any)?.limit ?? 10}
        onPageChange={setPage}
        onApprove={handleApprove}
        onCancel={handleCancel}
        onBlock={handleBlock}
        isApproving={approveMutation.isPending}
        isCancelling={cancelMutation.isPending}
        isBlocking={blockMutation.isPending}
        selectedIds={selectedIds}
        onSelectId={toggleSelectId}
        onSelectAll={toggleSelectAll}
      />
    </div>
  );
}
